import { Link } from 'react-router-dom';
import { 
  Globe, 
  ShoppingCart, 
  Users, 
  BarChart3,
  Code,
  Smartphone,
  TrendingUp
} from 'lucide-react';

export default function ServicesSection() {
  const services = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Sites Web Vitrine",
      description: "Présence en ligne professionnelle avec design responsive et moderne pour mettre en valeur votre entreprise.",
      features: ["Design personnalisé", "Mobile friendly", "SEO optimisé", "Performance"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "E-commerce",
      description: "Boutiques en ligne complètes avec gestion des stocks, paiements sécurisés et analyse des performances.",
      features: ["Paiements en ligne", "Gestion stock", "Dashboard admin", "Marketing"],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "CRM & Gestion",
      description: "Systèmes de gestion client et automatisation des processus métier pour optimiser vos opérations.",
      features: ["Gestion clients", "Facturation", "Rapports", "Automatisation"],
      color: "from-emerald-500 to-green-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Marketing Digital",
      description: "Stratégies digitales sur mesure pour augmenter votre visibilité et maximiser vos conversions.",
      features: ["SEO/SEA", "Social Media", "Analytics", "Campagnes"],
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Développement sur Mesure",
      description: "Applications web et mobiles personnalisées adaptées à vos besoins spécifiques.",
      features: ["Applications web", "APIs", "Intégrations", "Maintenance"],
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Applications Mobiles",
      description: "Applications iOS et Android natives ou cross-platform pour toucher vos clients partout.",
      features: ["iOS & Android", "UX optimisée", "Notifications", "App Stores"],
      color: "from-pink-500 to-rose-500"
    }
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-black to-gray-900" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
      
      <div className="relative container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <span className="text-sm font-semibold text-blue-300 uppercase">Nos Expertises</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Services <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Premium</span>
          </h2>
          
          <p className="text-xl text-gray-300 leading-relaxed">
            Des solutions digitales complètes pour transformer votre présence en ligne 
            et accélérer la croissance de votre entreprise.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div key={index} className="group relative">
              {/* Card Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 group-hover:border-blue-500/50 transition-colors duration-300" />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
              
              {/* Card Content */}
              <div className="relative p-8 h-full">
                {/* Icon with gradient */}
                <div className={`w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white`}>
                  {service.icon}
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <div className="w-2 h-2 rounded-full bg-green-400"></div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link 
                  to="/services" 
                  className="inline-flex items-center gap-2 text-blue-400 font-medium hover:text-blue-300 transition-colors duration-200 group/link"
                >
                  <span>Découvrir ce service</span>
                  <span className="group-hover/link:translate-x-1 transition-transform duration-200">→</span>
                </Link>
              </div>
              
              {/* Glow Effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 rounded-2xl" />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-3 px-8 py-4 bg-gray-800/50 text-gray-300 border border-gray-700 rounded-xl font-semibold hover:bg-gray-700/50 hover:text-white transition-all duration-200 hover:gap-4"
          >
            <span>Explorer tous nos services</span>
            <TrendingUp className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}